import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Github, ExternalLink, ChevronDown } from "lucide-react";
import SectionHeading from "../components/ui/SectionHeading";

const projectList = [
  {
    title: "Selenium Automation Framework",
    description: [
      "Built framework using Selenium Java + Maven + TestNG.",
      "Implemented Page Object Model (POM) for maintainability.",
      "Supports scalable and cross-browser testing.",
    ],
    techStack: ["Selenium", "Java", "Maven", "TestNG"],
    link: "https://github.com/im-gulshan/TestGenie",
    icon: <Github />,
  },
];

const ProjectCard = ({ project, index, isOpen, onToggle }) => (
  <motion.div
    layout
    transition={{ duration: 0.3, ease: "easeInOut" }}
    className={`relative rounded-xl border overflow-hidden transition-all duration-300 shadow-sm
      ${isOpen
        ? "border-blue-400 dark:border-blue-500 shadow-blue-100 dark:shadow-blue-900/20 shadow-md"
        : "border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-600 hover:shadow-md"
      }
      bg-white dark:bg-gray-800
    `}
  >
    {/* Header */}
    <div
      className="group flex items-start justify-between gap-4 pl-5 pr-4 py-4 cursor-pointer"
      onClick={() => onToggle(index)}
    >
      <div className="flex flex-col gap-1 min-w-0">
        <div className={`flex items-center font-semibold space-x-2 transition-colors duration-300 ${isOpen ? "text-blue-700 dark:text-blue-400" : "text-gray-800 dark:text-gray-200"}`}>
          <span className={`text-xl flex-shrink-0 transition-colors duration-300 ${isOpen ? "text-blue-500 dark:text-blue-400" : "text-gray-400 dark:text-gray-500 group-hover:text-blue-500 dark:group-hover:text-blue-400"}`}>
            {project.icon}
          </span>
          <span className="text-sm sm:text-base leading-snug">{project.title}</span>
        </div>
      </div>

      {/* Chevron pin to top right */}
      <motion.div
        animate={{ rotate: isOpen ? 180 : 0 }}
        transition={{ duration: 0.25, ease: "easeInOut" }}
        className={`flex-shrink-0 mt-0.5 transition-colors duration-300 ${isOpen ? "text-blue-500 dark:text-blue-400" : "text-gray-400 dark:text-gray-500 group-hover:text-blue-400"}`}
      >
        <ChevronDown size={20} />
      </motion.div>
    </div>

    {/* Expandable content */}
    <AnimatePresence initial={false}>
      {isOpen && (
        <motion.div
          key="content"
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
          className="overflow-hidden"
        >
          {/* Subtle top divider */}
          <div className="mx-4 border-t border-gray-100 dark:border-gray-700" />

          {/* Expanded body block */}
          <div className="pl-5 pr-4 pb-5 pt-3 bg-blue-50/30 dark:bg-blue-900/10">
            <ul className="space-y-2.5">
              {project.description.map((point, idx) => (
                <motion.li
                  key={`${index}-${idx}`}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.25, delay: idx * 0.04 }}
                  className="flex items-start gap-3 text-gray-700 dark:text-gray-300 text-sm sm:text-base leading-relaxed"
                >
                  <span className="flex-shrink-0 mt-2.5 w-1.5 h-1.5 rounded-full bg-blue-400 dark:bg-blue-500" />
                  <span>{point}</span>
                </motion.li>
              ))}
            </ul>

            {/* Tech Stack */}
            <div className="mt-4 flex flex-wrap gap-2">
              {project.techStack.map((tech, idx) => (
                <motion.span
                  key={idx}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.2, delay: idx * 0.03 }}
                  className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-700 transition-colors duration-300"
                >
                  {tech}
                </motion.span>
              ))}
            </div>

            {/* Link */}
            <div className="mt-4">
              <a
                href={project.link}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium inline-flex items-center transition-colors duration-300"
              >
                View Project
                <ExternalLink className="ml-2" size={16} />
              </a>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </motion.div>
);

const Projects = () => {
  const [expandedIndex, setExpandedIndex] = useState(0);

  const toggleProject = (index) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  return (
    <section
      id="projects"
      className="pt-4 pb-10 px-4 sm:px-6 md:px-16 bg-white dark:bg-gray-900 transition-colors duration-300"
    >
      <div className="container mx-auto max-w-screen-xl">
        <SectionHeading
          title="Projects"
          subtitle="Open-source frameworks I've built"
          gradient="blue"
        />

        <div className="space-y-4">
          {projectList.map((project, index) => (
            <ProjectCard
              key={index}
              project={project}
              index={index}
              isOpen={expandedIndex === index}
              onToggle={toggleProject}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Projects;